// Per-user console preferences — one small key/value store with two backings.
//
// localStorage always, so a logged-out console (and a bridge laptop that lost its
// connection) keeps its settings. The server's users.prefs JSONB additionally,
// when someone is signed in, so a preference follows the account across machines
// the way the panel layout already does. Writes go to both; on sign-in the
// server's copy wins (adoptServerPrefs), because "follows the user" is the whole
// point of the server copy.
//
// Deliberately generic: keys are strings, values are JSON, and subscribers are
// per-key. The clock (local/UTC) is the first tenant; a units preference lands
// here next by calling the same four functions, not by growing a sibling store.

import { putPrefs } from "../data/api.js";

const LS_KEY = "seastate_prefs_v1";

type PrefListener = (value: unknown) => void;

const values = new Map<string, unknown>();
const listeners = new Map<string, Set<PrefListener>>();

// Armed by the auth lifecycle (refreshAuth), never assumed: a logged-out console
// must work on localStorage alone, and a PUT fired without a session would just
// 401 into the log.
let serverSync = false;

/** The storage behind the store, or null where none exists (tests run this
 *  module under node; a bridge browser may block storage entirely). */
function storage(): Storage | null {
  try {
    if (typeof localStorage === "undefined") return null;
    return localStorage;
  } catch {
    return null;
  }
}

function persist(): void {
  const s = storage();
  if (s === null) return;
  try {
    s.setItem(LS_KEY, JSON.stringify(Object.fromEntries(values)));
  } catch {
    /* storage blocked or full — the preference still applies for the session */
  }
}

// Seed from localStorage once, at import.
{
  const s = storage();
  if (s !== null) {
    try {
      const raw = s.getItem(LS_KEY);
      const parsed: unknown = raw !== null ? JSON.parse(raw) : null;
      if (typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)) {
        for (const [k, v] of Object.entries(parsed)) values.set(k, v);
      }
    } catch {
      /* corrupt blob — start clean rather than throw at import */
    }
  }
}

function notify(key: string): void {
  const set = listeners.get(key);
  if (set === undefined) return;
  const v = values.get(key);
  // Copy first: a listener may unsubscribe during the notify.
  for (const fn of [...set]) fn(v);
}

/** The stored value for a key, or undefined. Callers narrow — an unknown value
 *  from an older blob (or another machine's newer client) must degrade to the
 *  default, never break. */
export function getPref(key: string): unknown {
  return values.get(key);
}

/** Set one preference: remember it, persist to localStorage, mirror to the
 *  server when signed in, and wake this key's subscribers. */
export function setPref(key: string, value: unknown): void {
  if (values.get(key) === value) return;
  values.set(key, value);
  persist();
  notify(key);
  if (serverSync) {
    // Fire-and-forget: the API layer never throws, and a failed mirror write
    // must not undo a preference that already applied locally.
    void putPrefs({ [key]: value });
  }
}

/** Subscribe to one key's changes. Returns the unsubscribe. */
export function subscribePref(key: string, fn: PrefListener): () => void {
  let set = listeners.get(key);
  if (set === undefined) {
    set = new Set();
    listeners.set(key, set);
  }
  set.add(fn);
  return () => {
    set.delete(fn);
  };
}

/** Adopt the server's users.prefs blob on sign-in. Server wins over whatever
 *  localStorage held, key by key; keys the server has never seen keep their
 *  local value. Nothing is written back up — adoption is a read. */
export function adoptServerPrefs(prefs: unknown): void {
  if (typeof prefs !== "object" || prefs === null || Array.isArray(prefs)) return;
  let changed = false;
  const touched: string[] = [];
  for (const [k, v] of Object.entries(prefs)) {
    if (values.get(k) === v) continue;
    values.set(k, v);
    touched.push(k);
    changed = true;
  }
  if (changed) persist();
  for (const k of touched) notify(k);
}

/** Arm (or disarm) the server mirror. The auth lifecycle owns this: on after a
 *  successful /api/me or login, off on sign-out. */
export function enableServerSync(on: boolean): void {
  serverSync = on;
}
